/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { AccessPolicy, AccessPolicyUpdate, AdminTrainingModuleRow } from "@/api/admin-api"

import { GraduationCap, Lock, Shield, ShieldCheck } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Skeleton } from "@/components/ui/skeleton"
import { AccessPolicyDialog } from "@/features/admin-dashboard/components/access-policy-dialog"
import { CollapsibleManagementSection } from "@/features/admin-dashboard/components/collapsible-management-section"
import { DelegationModeControl } from "@/features/admin-dashboard/components/delegation-mode-control"
import { EditTrainingModuleDialog } from "@/features/admin-dashboard/components/edit-training-module-dialog"
import { TrainingModuleBlock } from "@/features/admin-dashboard/components/training-catalog-blocks"
import { TrainingGatingRequirementsDialog } from "@/features/admin-dashboard/components/training-gating-dialog"
import {
  useAdminTrainingBaseAccessPolicyQuery,
  useAdminTrainingCatalogQuery,
  useAdminTrainingGatingQuery,
  useAdminTrainingModuleAccessPolicyQuery,
  useAdminTrainingSectionAccessPolicyQuery,
  useClearTrainingModuleAccessPolicyMutation,
  useClearTrainingSectionAccessPolicyMutation,
  useTrainingGatingRawQuery,
  useUpdateTrainingBaseAccessPolicyMutation,
  useUpdateTrainingGatingMutation,
  useUpdateTrainingGatingRawMutation,
  useUpdateTrainingModuleAccessPolicyMutation,
  useUpdateTrainingModuleStatusMutation,
  useUpdateTrainingSectionAccessPolicyMutation,
  useUpdateTrainingSectionStatusMutation,
} from "@/features/admin-dashboard/hooks/admin-queries"

function isPolicyConfigured(policy: AccessPolicy | undefined): boolean {
  const requires = policy?.requires
  const block = policy?.block
  return (
    !!requires?.oids?.length ||
    !!requires?.groups?.length ||
    !!block?.oids?.length ||
    !!block?.groups?.length ||
    policy?.unrestricted === true
  )
}

type AccessTarget =
  | null
  | { type: "module"; moduleId: string }
  | { type: "section"; moduleId: string; sectionId: string }

function renderTrainingBody({
  expandedModules,
  isLoading,
  modules,
  onEditModule,
  onToggleExpanded,
  onToggleModule,
  onToggleSection,
  setAccessTarget,
  t,
  updatingModuleId,
  updatingSectionKey,
}: {
  expandedModules: Record<string, boolean>
  isLoading: boolean
  modules: AdminTrainingModuleRow[]
  onEditModule: (moduleId: string) => void
  onToggleExpanded: (moduleId: string) => void
  onToggleModule: (moduleId: string, enabled: boolean) => void
  onToggleSection: (moduleId: string, sectionId: string, enabled: boolean) => void
  setAccessTarget: (target: AccessTarget) => void
  t: (key: string, opts?: { defaultValue?: string }) => string
  updatingModuleId: string | undefined
  updatingSectionKey: string | undefined
}) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between rounded-lg border p-4">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-6 w-12" />
          </div>
        ))}
      </div>
    )
  }
  if (modules.length === 0) {
    return (
      <EmptyState
        icon={GraduationCap}
        description={t("adminDashboard.trainingManagement.emptyDescription", {
          defaultValue: "No training modules in catalog.",
        })}
        title={t("adminDashboard.trainingManagement.emptyTitle", {
          defaultValue: "No training modules",
        })}
      />
    )
  }
  return (
    <div className="space-y-2">
      {modules.map((mod) => (
        <TrainingModuleBlock
          key={mod.id}
          expanded={expandedModules[mod.id] ?? false}
          mod={mod}
          updatingModuleId={updatingModuleId}
          updatingSectionKey={updatingSectionKey}
          onEditModule={onEditModule}
          onToggleModule={onToggleModule}
          onToggleSection={onToggleSection}
          onEditModuleAccess={() => {
            setAccessTarget({ type: "module", moduleId: mod.id })
          }}
          onEditSectionAccess={(sectionId) => {
            setAccessTarget({ type: "section", moduleId: mod.id, sectionId })
          }}
          onToggleExpanded={() => {
            onToggleExpanded(mod.id)
          }}
        />
      ))}
    </div>
  )
}

export function TrainingManagementCard() {
  const { t } = useTranslation()
  const [updatingModuleId, setUpdatingModuleId] = useState<string | undefined>()
  const [updatingSectionKey, setUpdatingSectionKey] = useState<string | undefined>()
  const [isBaseAccessDialogOpen, setIsBaseAccessDialogOpen] = useState(false)
  const [accessTarget, setAccessTarget] = useState<AccessTarget>(null)
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({})
  const [isGatingDialogOpen, setIsGatingDialogOpen] = useState(false)
  const [selectedGatingModules, setSelectedGatingModules] = useState<string[]>([])
  const [editingModuleId, setEditingModuleId] = useState<string | null>(null)

  const { data: catalog, isLoading } = useAdminTrainingCatalogQuery()
  const modules = catalog?.modules ?? []
  const tracks = catalog?.tracks ?? []

  const updateModuleMutation = useUpdateTrainingModuleStatusMutation()
  const updateSectionMutation = useUpdateTrainingSectionStatusMutation()
  const updateBaseAccessMutation = useUpdateTrainingBaseAccessPolicyMutation()
  const updateModuleAccessMutation = useUpdateTrainingModuleAccessPolicyMutation()
  const clearModuleAccessMutation = useClearTrainingModuleAccessPolicyMutation()
  const updateSectionAccessMutation = useUpdateTrainingSectionAccessPolicyMutation()
  const clearSectionAccessMutation = useClearTrainingSectionAccessPolicyMutation()
  const { data: gatingConfig, isLoading: isLoadingGating } = useAdminTrainingGatingQuery()
  const updateGatingMutation = useUpdateTrainingGatingMutation()
  // Raw-tab plumbing: a fresh raw doc + hash is fetched whenever the
  // dialog is open, then threaded into the dialog via rawValue/onRawSave
  // so the Raw tab actually appears (it's gated on those props being set).
  const { data: gatingRaw } = useTrainingGatingRawQuery(isGatingDialogOpen)
  const updateGatingRawMutation = useUpdateTrainingGatingRawMutation()

  useEffect(() => {
    if (isGatingDialogOpen && gatingConfig) {
      setSelectedGatingModules(gatingConfig.required_module_ids)
    }
  }, [isGatingDialogOpen, gatingConfig])

  const {
    data: baseAccessPolicy,
    isLoading: isLoadingBaseAccessPolicy,
    isError: isBaseAccessPolicyError,
  } = useAdminTrainingBaseAccessPolicyQuery(true)
  const {
    data: moduleAccessPolicy,
    isLoading: isLoadingModuleAccessPolicy,
    isError: isModuleAccessPolicyError,
  } = useAdminTrainingModuleAccessPolicyQuery(
    accessTarget?.type === "module" ? accessTarget.moduleId : undefined,
    accessTarget?.type === "module"
  )
  const {
    data: sectionAccessPolicy,
    isLoading: isLoadingSectionAccessPolicy,
    isError: isSectionAccessPolicyError,
  } = useAdminTrainingSectionAccessPolicyQuery(
    accessTarget?.type === "section" ? accessTarget.moduleId : undefined,
    accessTarget?.type === "section" ? accessTarget.sectionId : undefined,
    accessTarget?.type === "section"
  )

  const baseAccessConfigured = useMemo(() => {
    return isPolicyConfigured(baseAccessPolicy)
  }, [baseAccessPolicy])

  const selectedModule = useMemo(() => {
    if (accessTarget?.type !== "module") return undefined
    return modules.find((m) => m.id === accessTarget.moduleId)
  }, [accessTarget, modules])

  const selectedSection = useMemo(() => {
    if (accessTarget?.type !== "section") return undefined
    const mod = modules.find((m) => m.id === accessTarget.moduleId)
    return mod?.sections.find((s) => s.id === accessTarget.sectionId)
  }, [accessTarget, modules])

  const inheritForModule =
    selectedModule && accessTarget?.type === "module" ? !selectedModule.access_configured : true

  const inheritForSection =
    selectedSection && accessTarget?.type === "section" ? !selectedSection.access_configured : true

  const activeAccessPolicy =
    accessTarget?.type === "module" ? moduleAccessPolicy : sectionAccessPolicy
  const isLoadingAccessPolicy =
    accessTarget?.type === "module" ? isLoadingModuleAccessPolicy : isLoadingSectionAccessPolicy
  const isAccessPolicyError =
    accessTarget?.type === "module" ? isModuleAccessPolicyError : isSectionAccessPolicyError

  const handleToggleModule = async (moduleId: string, enabled: boolean) => {
    setUpdatingModuleId(moduleId)
    try {
      await updateModuleMutation.mutateAsync({ moduleId, enabled })
    } catch {
      toast.error(
        t("adminDashboard.trainingManagement.toggleError", {
          defaultValue: "Failed to update module status",
        })
      )
    } finally {
      setUpdatingModuleId(undefined)
    }
  }

  const handleToggleSection = async (moduleId: string, sectionId: string, enabled: boolean) => {
    const key = `${moduleId}:${sectionId}`
    setUpdatingSectionKey(key)
    try {
      await updateSectionMutation.mutateAsync({ moduleId, sectionId, enabled })
    } catch {
      toast.error(
        t("adminDashboard.trainingManagement.sectionToggleError", {
          defaultValue: "Failed to update section status",
        })
      )
    } finally {
      setUpdatingSectionKey(undefined)
    }
  }

  const toggleExpanded = (moduleId: string) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleId]: !(prev[moduleId] ?? false),
    }))
  }

  const handleSaveGating = async () => {
    try {
      await updateGatingMutation.mutateAsync({ required_module_ids: selectedGatingModules })
      toast.success(
        t("adminDashboard.trainingManagement.gatingSaveSuccess", {
          defaultValue: "Gating requirements updated",
        })
      )
      setIsGatingDialogOpen(false)
    } catch {
      toast.error(
        t("adminDashboard.trainingManagement.gatingSaveError", {
          defaultValue: "Failed to update gating requirements",
        })
      )
    }
  }

  const toggleGatingModule = (moduleId: string) => {
    setSelectedGatingModules((prev) =>
      prev.includes(moduleId) ? prev.filter((id) => id !== moduleId) : [...prev, moduleId]
    )
  }

  const toggleGatingTrack = (trackModuleIds: string[]) => {
    if (trackModuleIds.length === 0) return
    setSelectedGatingModules((prev) => {
      const allSelected = trackModuleIds.every((id) => prev.includes(id))
      if (allSelected) {
        return prev.filter((id) => !trackModuleIds.includes(id))
      }
      return Array.from(new Set([...prev, ...trackModuleIds]))
    })
  }

  return (
    <>
      <CollapsibleManagementSection
        icon={<GraduationCap className="size-5" />}
        id="training"
        storageKey="admin-management-training-expanded"
        title={t("adminDashboard.trainingManagement.title", "Training catalog")}
        headerActions={
          <div className="flex items-center gap-4">
            <Button
              className="flex items-center gap-2 px-3"
              disabled={isLoading}
              size="sm"
              type="button"
              variant="outline"
              onClick={() => setIsGatingDialogOpen(true)}
            >
              <Lock className="size-4" />
              {t("adminDashboard.trainingManagement.gatingRequirements", {
                defaultValue: "Gating",
              })}
            </Button>
            <Button
              className="flex items-center gap-2 px-3"
              disabled={isLoading}
              size="sm"
              type="button"
              variant="outline"
              onClick={() => setIsBaseAccessDialogOpen(true)}
            >
              {baseAccessConfigured ? (
                <ShieldCheck className="text-success size-4" />
              ) : (
                <Shield className="size-4" />
              )}
              {t("adminDashboard.trainingManagement.baseAccess", {
                defaultValue: "Access for all",
              })}
            </Button>
            <DelegationModeControl capability="training" />
          </div>
        }
      >
        {renderTrainingBody({
          expandedModules,
          isLoading,
          modules,
          onEditModule: (moduleId) => setEditingModuleId(moduleId),
          onToggleExpanded: toggleExpanded,
          onToggleModule: handleToggleModule,
          onToggleSection: handleToggleSection,
          setAccessTarget,
          t,
          updatingModuleId,
          updatingSectionKey,
        })}
      </CollapsibleManagementSection>

      <AccessPolicyDialog
        isLoading={isLoadingBaseAccessPolicy}
        isSaving={updateBaseAccessMutation.isPending}
        loadError={isBaseAccessPolicyError}
        open={isBaseAccessDialogOpen}
        policy={baseAccessPolicy}
        description={t("adminDashboard.trainingManagement.baseAccessDescription", {
          defaultValue:
            "Default access policy for training modules and sections unless overridden.",
        })}
        title={t("adminDashboard.trainingManagement.baseAccessTitle", {
          defaultValue: "Base access policy — Training",
        })}
        onOpenChange={setIsBaseAccessDialogOpen}
        onSave={async (policy: AccessPolicyUpdate) => {
          try {
            await updateBaseAccessMutation.mutateAsync({ policy })
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch {
            toast.error(
              t("adminDashboard.access.saveError", {
                defaultValue: "Failed to update access policy",
              })
            )
            throw new Error("save failed")
          }
        }}
      />

      <AccessPolicyDialog
        isLoading={isLoadingAccessPolicy}
        loadError={isAccessPolicyError || isBaseAccessPolicyError}
        open={accessTarget !== null}
        policy={activeAccessPolicy}
        description={t("adminDashboard.access.dialogDescription", {
          defaultValue: "Configure who can access this resource.",
        })}
        inherit={{
          enabled: true,
          inheritByDefault: accessTarget?.type === "module" ? inheritForModule : inheritForSection,
          policy: baseAccessPolicy,
          label: t("adminDashboard.trainingManagement.inheritBaseAccess", {
            defaultValue: "Inherit access for all",
          }),
          description: t("adminDashboard.trainingManagement.inheritBaseAccessDescription", {
            defaultValue:
              "When enabled, this resource uses the training baseline access policy. Disable to override.",
          }),
          onSaveInherited: async () => {
            if (accessTarget?.type === "module") {
              await clearModuleAccessMutation.mutateAsync({ moduleId: accessTarget.moduleId })
            } else if (accessTarget?.type === "section") {
              await clearSectionAccessMutation.mutateAsync({
                moduleId: accessTarget.moduleId,
                sectionId: accessTarget.sectionId,
              })
            }
          },
        }}
        isSaving={
          updateModuleAccessMutation.isPending ||
          updateSectionAccessMutation.isPending ||
          clearModuleAccessMutation.isPending ||
          clearSectionAccessMutation.isPending
        }
        title={t("adminDashboard.access.dialogTitle", {
          defaultValue: "Access policy — {{name}}",
          name:
            accessTarget?.type === "module"
              ? (selectedModule?.display_name ?? accessTarget.moduleId)
              : (selectedSection?.display_name ?? accessTarget?.sectionId ?? ""),
        })}
        onOpenChange={(open) => {
          if (!open) {
            setAccessTarget(null)
          }
        }}
        onSave={async (policy: AccessPolicyUpdate) => {
          try {
            if (accessTarget?.type === "module") {
              await updateModuleAccessMutation.mutateAsync({
                moduleId: accessTarget.moduleId,
                policy,
              })
            } else if (accessTarget?.type === "section") {
              await updateSectionAccessMutation.mutateAsync({
                moduleId: accessTarget.moduleId,
                sectionId: accessTarget.sectionId,
                policy,
              })
            }
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch {
            toast.error(
              t("adminDashboard.access.saveError", {
                defaultValue: "Failed to update access policy",
              })
            )
            throw new Error("save failed")
          }
        }}
      />

      <TrainingGatingRequirementsDialog
        isLoadingGating={isLoadingGating}
        isSaving={updateGatingMutation.isPending || updateGatingRawMutation.isPending}
        modules={modules}
        open={isGatingDialogOpen}
        rawValue={gatingRaw?.entity}
        selectedModuleIds={selectedGatingModules}
        tracks={tracks}
        onOpenChange={setIsGatingDialogOpen}
        onSave={handleSaveGating}
        onToggleModule={toggleGatingModule}
        onToggleTrack={toggleGatingTrack}
        onRawSave={async (next, reason) => {
          if (!gatingRaw?.hash) {
            throw new Error(
              t("adminDashboard.trainingManagement.gatingRawNotLoaded", {
                defaultValue: "Raw value not loaded yet",
              })
            )
          }
          // Reject malformed shapes loudly instead of coercing to an empty
          // list — silently saving ``{required_module_ids: []}`` because
          // the admin mistyped the property would wipe the gating config
          // while the dialog reports success.
          if (typeof next !== "object" || next === null || Array.isArray(next)) {
            throw new Error(
              t("adminDashboard.trainingManagement.gatingRawInvalidShape", {
                defaultValue: "Expected an object with `required_module_ids`",
              })
            )
          }
          const parsed = next as { required_module_ids?: unknown }
          if (!Array.isArray(parsed.required_module_ids)) {
            throw new Error(
              t("adminDashboard.trainingManagement.gatingRawNotArray", {
                defaultValue: "`required_module_ids` must be an array of strings",
              })
            )
          }
          if (!parsed.required_module_ids.every((id) => typeof id === "string")) {
            throw new Error(
              t("adminDashboard.trainingManagement.gatingRawNotStrings", {
                defaultValue: "`required_module_ids` entries must all be strings",
              })
            )
          }
          await updateGatingRawMutation.mutateAsync({
            entity: { required_module_ids: parsed.required_module_ids },
            expectedHash: gatingRaw.hash,
            reason,
          })
          toast.success(
            t("adminDashboard.trainingManagement.gatingSaveSuccess", {
              defaultValue: "Gating requirements updated",
            })
          )
          setIsGatingDialogOpen(false)
        }}
      />

      <EditTrainingModuleDialog
        isOpen={editingModuleId !== null}
        moduleId={editingModuleId}
        onOpenChange={(open) => {
          if (!open) setEditingModuleId(null)
        }}
      />
    </>
  )
}
